/**
 * useAppBadge.js — Hook para el badge numérico del icono PWA de StarTip.
 *
 * Usa la Web App Badging API (navigator.setAppBadge / clearAppBadge).
 * Compatible con Chrome 81+, Edge 81+, Samsung Internet 15+.
 * En iOS / Firefox se ignora silenciosamente.
 *
 * Comportamiento:
 *  - Al montar: consulta GET /api/notifications/badge-count y actualiza el badge.
 *  - Cada 30s (mientras la pestaña es visible): refresca el conteo.
 *  - Al ganar foco / salir de background: refresca el conteo.
 *  - Si el conteo llega a 0: llama clearAppBadge().
 *  - No hace nada si el usuario no está autenticado.
 */

import { useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

const POLL_MS = 30_000;

/** Returns the API base URL — always uses current domain at runtime. */
function apiBase() {
  return typeof window !== "undefined"
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || "");
}

/** Returns true if the Badging API is available in this browser. */
function isBadgingSupported() {
  return typeof navigator !== "undefined" && "setAppBadge" in navigator;
}

export function useAppBadge() {
  const { user } = useAuth();

  /** Fetch unread count and update the app badge. */
  const syncBadge = useCallback(async () => {
    if (!user || !isBadgingSupported()) return;
    try {
      const res = await fetch(`${apiBase()}/api/notifications/badge-count`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const { count } = await res.json();
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
    } catch {
      /* silent — badge is non-critical */
    }
  }, [user]);

  /** Clear the badge immediately (call when user reads all messages). */
  const clearBadge = useCallback(async () => {
    if (!isBadgingSupported()) return;
    try {
      await navigator.clearAppBadge();
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!user) return;

    // 1. Sync on mount
    syncBadge();

    // 2. Sync when the tab becomes visible again (app returns from background)
    const onVisibilityChange = () => {
      if (!document.hidden) syncBadge();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // 3. Sync on window focus
    window.addEventListener("focus", syncBadge);

    // 4. Poll every 30s while the tab is visible
    const intervalId = setInterval(() => {
      if (!document.hidden) syncBadge();
    }, POLL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", syncBadge);
      clearInterval(intervalId);
    };
  }, [user, syncBadge]);

  return { syncBadge, clearBadge };
}
